/**
 * Situação de cada peça da biometria e os contadores por mão.
 *
 * Fora do componente porque é regra de leitura, não desenho: decide o que um item É a partir do que
 * foi capturado e do que foi marcado como anômalo. O nome da classe de CSS não vive aqui — ele é
 * derivado do próprio valor no template, como já se faz no marcador da timeline do kanban.
 */

/** O que o selo do card anuncia. Três estados fechados, os mesmos da legenda do rodapé. */
export enum ESituacaoBiometria {
    /** Imagem presente e sem ressalva. */
    CAPTURADA = 'capturada',
    /** Marcada como anômala. Vence a captura: é a informação que muda a conduta de quem confere. */
    ANOMALIA = 'anomalia',
    /** Nada capturado. */
    AUSENTE = 'ausente'
}

const ROTULO: Record<ESituacaoBiometria, string> = {
    [ESituacaoBiometria.CAPTURADA]: 'Capturada',
    [ESituacaoBiometria.ANOMALIA]: 'Anomalia',
    [ESituacaoBiometria.AUSENTE]: 'Não capturada'
};

const ICONE: Record<ESituacaoBiometria, string> = {
    [ESituacaoBiometria.CAPTURADA]: 'pi pi-check-circle',
    [ESituacaoBiometria.ANOMALIA]: 'pi pi-exclamation-triangle',
    [ESituacaoBiometria.AUSENTE]: 'pi pi-circle'
};

/** Quanto de uma mão está pronto — o que os selos do cabeçalho resumem. */
export interface ResumoDaMao {
    capturadas: number;
    total: number;
    anomalias: number;
}

/**
 * Situação de uma peça.
 *
 * A anomalia vence a captura de propósito: um card verde "Capturada" com a ressalva escondida num
 * checkbox abaixo esconderia justamente o que faz alguém olhar duas vezes.
 */
export function situacaoDaPeca(temImagem: boolean, temAnomalia: boolean): ESituacaoBiometria {
    if (temAnomalia) {
        return ESituacaoBiometria.ANOMALIA;
    }
    return temImagem ? ESituacaoBiometria.CAPTURADA : ESituacaoBiometria.AUSENTE;
}

export function rotuloDaSituacao(situacao: ESituacaoBiometria): string {
    return ROTULO[situacao];
}

export function iconeDaSituacao(situacao: ESituacaoBiometria): string {
    return ICONE[situacao];
}

/**
 * Contadores de uma mão.
 *
 * `capturadas` conta imagem presente, INCLUSIVE a marcada como anômala: a digital existe e foi
 * coletada — a ressalva é sobre a qualidade dela, e por isso vai no contador próprio. Somar os dois
 * como se fossem exclusivos faria "3 / 5" numa mão com as cinco digitais no lugar.
 */
export function resumoDaMao<Dedo extends string>(imagens: readonly (string | undefined)[], anomalias: Readonly<Partial<Record<Dedo, boolean>>>, dedos: readonly Dedo[]): ResumoDaMao {
    const capturadas = dedos.reduce((total, _dedo, indice) => (imagens[indice] ? total + 1 : total), 0);
    const comAnomalia = dedos.reduce((total, dedo) => (anomalias[dedo] ? total + 1 : total), 0);

    return { capturadas, total: dedos.length, anomalias: comAnomalia };
}
